import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getPostBySlug, toggleLike, getPublicComments, addComment, getPublishedPosts } from "../../api/blogAPI";
import { getSettings } from "../../api/settingsAPI";
import { trackVisit } from "../../api/analyticsAPI";
import { getProfile } from "../../api/profileAPI";

const BASE = process.env.REACT_APP_EXPRESS_API_BASE_URL || "http://localhost:3001";

const resolveImg = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${BASE}${url}`;
};

const renderContent = (content) =>
  (content || "").replace(/src="(\/uploads\/[^"]+)"/g, `src="${BASE}$1"`);

const fmtDate = (d, long) =>
  d ? new Date(d).toLocaleDateString("en-US", long
    ? { month: "long", day: "numeric", year: "numeric" }
    : { month: "short", day: "numeric", year: "numeric" }) : null;

// ─── Sidebar: Artikel Terbaru ────────────────────────────────────────────────
const RecentPostsSidebar = ({ currentSlug, allPosts, theme }) => {
  const recent = allPosts
    .filter((p) => p.slug !== currentSlug)
    .slice(0, 5);

  const styles = {
    classic: {
      wrap: "bg-[#111116] border border-zinc-800 rounded-2xl p-5",
      accent: "bg-blue-500",
      heading: "text-zinc-400",
      item: "text-zinc-200 hover:text-white",
      date: "text-zinc-600",
      num: "bg-zinc-800 text-zinc-500",
      divider: "border-zinc-800",
    },
    modern: {
      wrap: "bg-white/3 border border-white/8 rounded-2xl p-5",
      accent: "bg-purple-500",
      heading: "text-zinc-400",
      item: "text-zinc-200 hover:text-white",
      date: "text-zinc-500",
      num: "bg-white/10 text-zinc-500",
      divider: "border-white/8",
    },
    terminal: {
      wrap: "bg-[#0a0e0a] border border-green-500/30 rounded-lg p-5 font-mono",
      accent: "bg-green-500",
      heading: "text-green-600",
      item: "text-green-300 hover:text-green-200",
      date: "text-green-700",
      num: "bg-green-900/30 text-green-600",
      divider: "border-green-500/20",
    },
    fabrica: {
      wrap: "bg-white border border-stone-200 rounded-2xl p-5",
      accent: "bg-[#E85D04]",
      heading: "text-stone-400",
      item: "text-stone-700 hover:text-stone-900",
      date: "text-stone-400",
      num: "bg-stone-100 text-stone-500",
      divider: "border-stone-100",
    },
    aurora: {
      wrap: "bg-[rgba(15,23,42,0.7)] border border-[rgba(148,163,184,0.12)] rounded-2xl p-5 backdrop-blur-md",
      accent: "bg-[#22C55E]",
      heading: "text-slate-400",
      item: "text-slate-300 hover:text-white",
      date: "text-slate-500",
      num: "bg-[rgba(34,197,94,0.1)] text-[#22C55E]",
      divider: "border-[rgba(148,163,184,0.1)]",
    },
    vanta: {
      wrap: "p-5 border border-[rgba(201,168,76,0.15)]",
      accent: "bg-[#C9A84C]",
      heading: "text-[#8B9BB4]",
      item: "text-[#F5ECD7] hover:text-[#C9A84C]",
      date: "text-[#4A5568]",
      num: "bg-[rgba(201,168,76,0.1)] text-[#C9A84C]",
      divider: "border-[rgba(201,168,76,0.1)]",
    },
  };
  const s = styles[theme] || styles.classic;
  const term = theme === "terminal";

  return (
    <div className={s.wrap}>
      <div className="flex items-center gap-2 mb-4">
        <span className={`w-1 h-4 rounded-full ${s.accent}`} />
        <h3 className={`text-xs font-black uppercase tracking-widest ${s.heading} ${term ? "normal-case tracking-normal" : ""}`}>
          {term ? "# recent_posts" : "Artikel Terbaru"}
        </h3>
      </div>
      <div className="space-y-3">
        {recent.map((p, i) => (
          <div key={p.slug}>
            <Link to={`/blog/${p.slug}`} className="flex items-start gap-3 group">
              <span className={`flex-shrink-0 w-6 h-6 rounded-md text-[10px] font-bold flex items-center justify-center mt-0.5 ${s.num}`}>
                {String(i + 1).padStart(2, "0")}
              </span>
              <div className="min-w-0">
                <p className={`text-sm font-semibold leading-snug transition-colors line-clamp-2 ${s.item}`}>
                  {p.title}
                </p>
                {p.published_at && (
                  <p className={`text-[11px] mt-1 ${s.date}`}>{fmtDate(p.published_at)}</p>
                )}
              </div>
            </Link>
            {i < recent.length - 1 && <div className={`mt-3 border-t ${s.divider}`} />}
          </div>
        ))}
        {recent.length === 0 && (
          <p className={`text-xs ${s.date}`}>{term ? "// no other posts" : "Belum ada artikel lain."}</p>
        )}
      </div>
    </div>
  );
};

// ─── Sidebar: LinkedIn CTA ────────────────────────────────────────────────────
const LinkedInSidebar = ({ theme, profile }) => {
  const linkedinUrl = profile?.show_linkedin ? profile?.social_linkedin : null;
  const styles = {
    classic: {
      wrap: "bg-[#111116] border border-zinc-800 rounded-2xl p-5",
      accent: "bg-blue-500",
      heading: "text-zinc-400",
      body: "text-zinc-400",
      btn: "bg-blue-600 hover:bg-blue-500 text-white",
      icon: "text-blue-400",
    },
    modern: {
      wrap: "bg-white/3 border border-white/8 rounded-2xl p-5",
      accent: "bg-purple-500",
      heading: "text-zinc-400",
      body: "text-zinc-400",
      btn: "bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white",
      icon: "text-purple-400",
    },
    terminal: {
      wrap: "bg-[#0a0e0a] border border-green-500/30 rounded-lg p-5 font-mono",
      accent: "bg-green-500",
      heading: "text-green-600",
      body: "text-green-700",
      btn: "bg-green-500/10 border border-green-500/50 hover:bg-green-500/20 text-green-400",
      icon: "text-green-500",
    },
    fabrica: {
      wrap: "bg-white border border-stone-200 rounded-2xl p-5",
      accent: "bg-[#E85D04]",
      heading: "text-stone-400",
      body: "text-stone-500",
      btn: "bg-[#E85D04] hover:opacity-90 text-white",
      icon: "text-[#E85D04]",
    },
    aurora: {
      wrap: "bg-[rgba(15,23,42,0.7)] border border-[rgba(148,163,184,0.12)] rounded-2xl p-5 backdrop-blur-md",
      accent: "bg-[#22C55E]",
      heading: "text-slate-400",
      body: "text-slate-400",
      btn: "bg-[rgba(34,197,94,0.15)] border border-[rgba(34,197,94,0.3)] hover:bg-[rgba(34,197,94,0.25)] text-[#22C55E]",
      icon: "text-[#22C55E]",
    },
    vanta: {
      wrap: "p-5 border border-[rgba(201,168,76,0.15)]",
      accent: "bg-[#C9A84C]",
      heading: "text-[#8B9BB4]",
      body: "text-[#4A5568]",
      btn: "bg-[rgba(201,168,76,0.15)] border border-[rgba(201,168,76,0.3)] hover:bg-[rgba(201,168,76,0.25)] text-[#C9A84C]",
      icon: "text-[#C9A84C]",
    },
  };
  const s = styles[theme] || styles.classic;
  const term = theme === "terminal";

  return (
    <div className={s.wrap}>
      <div className="flex items-center gap-2 mb-3">
        <span className={`w-1 h-4 rounded-full ${s.accent}`} />
        <h3 className={`text-xs font-black uppercase tracking-widest ${s.heading} ${term ? "normal-case tracking-normal" : ""}`}>
          {term ? "# open_to_work" : "Terbuka untuk Peluang"}
        </h3>
      </div>
      <p className={`text-xs leading-relaxed mb-4 ${s.body}`}>
        {term
          ? "// lagi cari kolaborasi atau lowongan? cek profil gue"
          : "Lagi cari kandidat atau ingin berkolaborasi? Cek profil saya di LinkedIn."}
      </p>
      {linkedinUrl && (
        <a
          href={linkedinUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-sm font-bold transition-all ${s.btn}`}
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
          </svg>
          {term ? "./open_linkedin.sh" : "View LinkedIn Profile"}
        </a>
      )}
    </div>
  );
};

// ─── Related Posts (bottom, random) ──────────────────────────────────────────
const RelatedPosts = ({ currentSlug, allPosts, theme }) => {
  const pool = allPosts.filter((p) => p.slug !== currentSlug);
  const shuffled = [...pool].sort(() => Math.random() - 0.5).slice(0, 3);

  if (shuffled.length === 0) return null;

  const styles = {
    classic: {
      section: "mt-14",
      accent: "bg-blue-500",
      heading: "text-zinc-400",
      label: "text-blue-400",
      card: "bg-[#111116] border border-zinc-800 hover:border-zinc-600",
      title: "text-white",
      date: "text-zinc-500",
      link: "text-blue-400 hover:text-blue-300",
    },
    modern: {
      section: "mt-14",
      accent: "bg-purple-500",
      heading: "text-zinc-400",
      label: "text-purple-400",
      card: "bg-white/3 border border-white/8 hover:border-purple-500/30",
      title: "text-white",
      date: "text-zinc-500",
      link: "text-purple-400 hover:text-purple-300",
    },
    terminal: {
      section: "mt-10",
      accent: "bg-green-500",
      heading: "text-green-600",
      label: "text-green-500",
      card: "bg-[#0a0e0a] border border-green-500/30 hover:border-green-400/50 font-mono",
      title: "text-green-300",
      date: "text-green-700",
      link: "text-green-400 hover:text-green-300",
    },
    fabrica: {
      section: "mt-14",
      accent: "bg-[#E85D04]",
      heading: "text-stone-400",
      label: "text-[#E85D04]",
      card: "bg-white border border-stone-200 hover:border-stone-400 shadow-sm hover:shadow-md",
      title: "text-stone-900",
      date: "text-stone-400",
      link: "text-[#E85D04] hover:opacity-70",
    },
    aurora: {
      section: "mt-14",
      accent: "bg-[#22C55E]",
      heading: "text-slate-400",
      label: "text-[#22C55E]",
      card: "bg-[rgba(15,23,42,0.7)] border border-[rgba(148,163,184,0.12)] hover:border-[rgba(34,197,94,0.3)] backdrop-blur-md",
      title: "text-white",
      date: "text-slate-500",
      link: "text-[#22C55E] hover:opacity-70",
    },
    vanta: {
      section: "mt-14",
      accent: "bg-[#C9A84C]",
      heading: "text-[#8B9BB4]",
      label: "text-[#C9A84C]",
      card: "border border-[rgba(201,168,76,0.15)] hover:border-[rgba(201,168,76,0.4)] hover:-translate-y-1.5 transition-all duration-300",
      title: "text-[#F5ECD7]",
      date: "text-[#4A5568]",
      link: "text-[#C9A84C] hover:opacity-70",
    },
  };
  const s = styles[theme] || styles.classic;
  const term = theme === "terminal";

  return (
    <div className={s.section}>
      <div className="flex items-center gap-3 mb-6">
        <span className={`w-1 h-5 rounded-full ${s.accent}`} />
        <div>
          <p className={`text-[10px] font-bold uppercase tracking-widest ${s.label}`}>
            {term ? "$ ls ./related" : "You may also like"}
          </p>
          <h2 className={`text-lg font-black ${s.heading} ${term ? "font-mono text-base" : ""}`}>
            {term ? "# related_articles" : "Artikel Terkait"}
          </h2>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {shuffled.map((p) => (
          <Link key={p.slug} to={`/blog/${p.slug}`}
            className={`block rounded-2xl overflow-hidden transition-all ${s.card}`}>
            {p.cover_img && (
              <div className="h-36 overflow-hidden">
                <img src={resolveImg(p.cover_img)} alt={p.title}
                  className={`w-full h-full object-cover ${term ? "grayscale contrast-125" : ""}`} />
              </div>
            )}
            <div className="p-4">
              {p.published_at && (
                <p className={`text-[10px] font-mono mb-1 ${s.date}`}>{fmtDate(p.published_at, true)}</p>
              )}
              <p className={`text-sm font-bold leading-snug line-clamp-2 mb-3 ${s.title}`}>{p.title}</p>
              <span className={`text-xs font-semibold ${s.link}`}>
                {term ? "cat article.md →" : "Baca artikel →"}
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// CLASSIC
// ════════════════════════════════════════════════════════════════════════════
const ClassicLayout = (p) => {
  const { post, cover, liked, likeCount, onLike, comments, allPosts, ...c } = p;
  return (
    <div className="min-h-screen bg-[#0d0d10]">
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_top,rgba(59,130,246,0.05)_0%,transparent_60%)] pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="mb-8 flex items-center gap-4">
          <Link to="/blog" className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm font-semibold transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            Blog
          </Link>
          <div className="flex-1 h-px bg-zinc-800" />
        </div>
        <div className="flex gap-8 items-start">
          {/* Main content */}
          <div className="flex-1 min-w-0">
            {cover && <div className="mb-8 rounded-2xl overflow-hidden h-56 sm:h-72 border border-zinc-800"><img src={cover} alt={post.title} className="w-full h-full object-cover" /></div>}
            <div className="mb-8">
              <h1 className="text-2xl sm:text-3xl font-black text-white leading-tight">{post.title}</h1>
              <div className="flex items-center gap-3 mt-3 flex-wrap">
                {post.published_at && <span className="text-xs text-zinc-500 font-mono">{fmtDate(post.published_at, true)}</span>}
                {post.view_count != null && <><span className="text-zinc-700">·</span><span className="text-xs text-zinc-500">{post.view_count} views</span></>}
              </div>
            </div>
            <article className="prose prose-invert prose-sm max-w-none prose-headings:font-black prose-a:text-blue-400 prose-code:bg-zinc-800 prose-code:px-1 prose-code:rounded prose-pre:bg-zinc-900 prose-pre:border prose-pre:border-zinc-800"
              dangerouslySetInnerHTML={{ __html: renderContent(post.content) }} />
            <div className="mt-10">
              <LikeButton liked={liked} likeCount={likeCount} onLike={onLike}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm border transition-all ${liked ? "bg-red-600/20 border-red-500/50 text-red-400" : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-red-500/40 hover:text-red-400"}`} />
            </div>
            <RelatedPosts currentSlug={post.slug} allPosts={allPosts} theme="classic" />
            <CommentsBlock {...c} comments={comments} theme="classic" />
          </div>
          {/* Sidebar */}
          <aside className="hidden lg:flex flex-col gap-5 w-72 flex-shrink-0 sticky top-8">
            <RecentPostsSidebar currentSlug={post.slug} allPosts={allPosts} theme="classic" />
            <LinkedInSidebar theme="classic" profile={p.profile} />
          </aside>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// MODERN — full-width cover hero, centered article, purple
// ════════════════════════════════════════════════════════════════════════════
const ModernLayout = (p) => {
  const { post, cover, liked, likeCount, onLike, comments, allPosts, profile, ...c } = p;
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {cover ? (
        <div className="relative h-72 sm:h-96 w-full overflow-hidden">
          <img src={cover} alt={post.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-slate-950/60" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-10 w-full">
              <Link to="/blog" className="inline-flex items-center gap-2 text-zinc-300 hover:text-white text-sm font-semibold mb-4 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg> Blog
              </Link>
              <h1 className="text-3xl sm:text-4xl font-black leading-tight drop-shadow-lg">{post.title}</h1>
              <div className="flex items-center gap-3 mt-3 text-xs text-zinc-300 font-mono">
                {fmtDate(post.published_at, true)}
                {post.view_count != null && <><span>·</span>{post.view_count} views</>}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 pb-4">
          <Link to="/blog" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white text-sm font-semibold mb-6 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg> Blog
          </Link>
          <h1 className="text-3xl sm:text-4xl font-black leading-tight">{post.title}</h1>
          <div className="flex items-center gap-3 mt-3 text-xs text-zinc-500 font-mono">
            {fmtDate(post.published_at, true)}
            {post.view_count != null && <><span>·</span>{post.view_count} views</>}
          </div>
        </div>
      )}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex gap-8 items-start">
          {/* Main content */}
          <div className="flex-1 min-w-0">
            <article className="prose prose-invert max-w-none prose-headings:font-black prose-a:text-purple-400 prose-code:bg-purple-950/40 prose-code:px-1 prose-code:rounded prose-pre:bg-slate-900 prose-pre:border prose-pre:border-purple-900/30"
              dangerouslySetInnerHTML={{ __html: renderContent(post.content) }} />
            <div className="mt-10">
              <LikeButton liked={liked} likeCount={likeCount} onLike={onLike}
                className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-bold text-sm transition-all ${liked ? "bg-gradient-to-r from-red-600 to-pink-600 text-white shadow-lg shadow-red-900/30" : "bg-white/5 border border-white/10 text-zinc-300 hover:border-purple-500/40 hover:text-purple-300"}`} />
            </div>
            <RelatedPosts currentSlug={post.slug} allPosts={allPosts} theme="modern" />
            <CommentsBlock {...c} comments={comments} theme="modern" />
          </div>
          {/* Sidebar */}
          <aside className="hidden lg:flex flex-col gap-5 w-72 flex-shrink-0 sticky top-8">
            <RecentPostsSidebar currentSlug={post.slug} allPosts={allPosts} theme="modern" />
            <LinkedInSidebar theme="modern" profile={profile} />
          </aside>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// TERMINAL — `cat post.md` in a terminal window
// ════════════════════════════════════════════════════════════════════════════
const TerminalLayout = (p) => {
  const { post, cover, liked, likeCount, onLike, comments, allPosts, profile, ...c } = p;
  return (
    <div className="min-h-screen bg-black text-green-400 font-mono relative">
      <div className="fixed inset-0 pointer-events-none z-40 opacity-[0.07]"
        style={{ backgroundImage: "repeating-linear-gradient(0deg, #22c55e 0px, #22c55e 1px, transparent 1px, transparent 3px)" }} />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <Link to="/blog" className="inline-flex items-center gap-1 text-green-600 hover:text-green-400 text-sm mb-6 transition-colors">
          <span className="text-green-800">$</span> cd ~/blog
        </Link>
        <div className="flex gap-8 items-start">
          {/* Main content */}
          <div className="flex-1 min-w-0">
            <div className="rounded-lg border border-green-500/30 bg-[#0a0e0a] overflow-hidden">
              <div className="flex items-center gap-2 px-3 py-2 bg-[#0d130d] border-b border-green-500/20">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
                <span className="ml-2 text-[11px] text-green-700 truncate">cat {post.slug}.md</span>
              </div>
              <div className="p-4 sm:p-6">
                <p className="text-green-500 text-sm mb-4">
                  <span className="text-green-700">affan@portfolio</span><span className="text-green-800">:~/blog$ </span>cat {post.slug}.md
                </p>
                <p className="text-green-700 text-xs mb-1"># published: {fmtDate(post.published_at, true) || "draft"} · {post.view_count ?? 0} views</p>
                <h1 className="text-xl sm:text-2xl font-bold text-green-300 mb-5"># {post.title}</h1>
                {cover && <div className="mb-5 rounded border border-green-500/30 overflow-hidden grayscale contrast-125"><img src={cover} alt={post.title} className="w-full h-auto mix-blend-luminosity" /></div>}
                <article className="prose prose-invert prose-sm max-w-none prose-p:text-green-300 prose-headings:text-green-400 prose-headings:font-bold prose-strong:text-green-300 prose-a:text-green-400 prose-li:text-green-300 prose-code:text-green-300 prose-code:bg-green-950/40 prose-code:px-1 prose-code:rounded prose-pre:bg-[#060906] prose-pre:border prose-pre:border-green-500/20 prose-img:rounded prose-img:border prose-img:border-green-500/20"
                  dangerouslySetInnerHTML={{ __html: renderContent(post.content) }} />
              </div>
            </div>
            <div className="mt-6">
              <LikeButton liked={liked} likeCount={likeCount} onLike={onLike} label="star"
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded text-sm font-bold border transition-all ${liked ? "bg-green-500/20 border-green-400 text-green-300" : "bg-transparent border-green-700 text-green-500 hover:border-green-400 hover:text-green-400"}`} />
            </div>
            <RelatedPosts currentSlug={post.slug} allPosts={allPosts} theme="terminal" />
            <CommentsBlock {...c} comments={comments} theme="terminal" />
          </div>
          {/* Sidebar */}
          <aside className="hidden lg:flex flex-col gap-5 w-72 flex-shrink-0 sticky top-8">
            <RecentPostsSidebar currentSlug={post.slug} allPosts={allPosts} theme="terminal" />
            <LinkedInSidebar theme="terminal" profile={profile} />
          </aside>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// FABRICA — editorial clean, warm off-white, orange accent
// ════════════════════════════════════════════════════════════════════════════
const FabricaLayout = (p) => {
  const { post, cover, liked, likeCount, onLike, comments, allPosts, profile, ...c } = p;
  const ACCENT = "#E85D04";
  return (
    <div className="min-h-screen text-stone-900" style={{ backgroundColor: "#FAFAF8" }}>
      {/* Full-width cover */}
      {cover && (
        <div className="relative h-64 sm:h-96 w-full overflow-hidden border-b border-stone-200">
          <img src={cover} alt={post.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#FAFAF8] via-transparent to-transparent" />
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-stone-400 hover:text-stone-800 text-sm font-semibold transition-colors mb-8"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to blog
        </Link>

        {/* Title block */}
        {!cover && (
          <div className="mb-8">
            <h1 className="text-3xl sm:text-4xl font-black text-stone-900 leading-tight mb-3">{post.title}</h1>
            <div className="flex items-center gap-3 text-xs text-stone-400">
              {fmtDate(post.published_at, true)}
              {post.view_count != null && <><span>·</span>{post.view_count} views</>}
            </div>
          </div>
        )}

        {cover && (
          <div className="mb-8">
            <h1 className="text-3xl sm:text-4xl font-black text-stone-900 leading-tight mb-3">{post.title}</h1>
            <div className="flex items-center gap-3 text-xs text-stone-400">
              {fmtDate(post.published_at, true)}
              {post.view_count != null && <><span>·</span>{post.view_count} views</>}
            </div>
          </div>
        )}

        <div className="flex gap-8 items-start">
          {/* Main content */}
          <div className="flex-1 min-w-0">
            <article
              className="prose max-w-none prose-headings:font-black prose-headings:text-stone-900 prose-headings:tracking-tight prose-a:font-semibold prose-code:bg-stone-100 prose-code:text-stone-700 prose-code:px-1 prose-code:rounded prose-pre:bg-stone-900 prose-pre:border prose-pre:border-stone-800 prose-img:rounded-2xl prose-img:border prose-img:border-stone-200 prose-blockquote:border-l-[#E85D04] prose-blockquote:text-stone-600"
              style={{ "--tw-prose-links": ACCENT }}
              dangerouslySetInnerHTML={{ __html: renderContent(post.content) }}
            />
            <div className="mt-10">
              <LikeButton
                liked={liked}
                likeCount={likeCount}
                onLike={onLike}
                className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-bold text-sm transition-all border ${
                  liked
                    ? "text-white border-transparent"
                    : "bg-white border-stone-200 text-stone-500 hover:border-stone-400"
                }`}
                style={liked ? { backgroundColor: ACCENT, borderColor: ACCENT } : {}}
              />
            </div>
            <RelatedPosts currentSlug={post.slug} allPosts={allPosts} theme="fabrica" />
            <CommentsBlock {...c} comments={comments} theme="fabrica" />
          </div>

          {/* Sidebar */}
          <aside className="hidden lg:flex flex-col gap-5 w-72 flex-shrink-0 sticky top-8">
            <RecentPostsSidebar currentSlug={post.slug} allPosts={allPosts} theme="fabrica" />
            <LinkedInSidebar theme="fabrica" profile={profile} />
          </aside>
        </div>
      </div>
    </div>
  );
};

// ─── Shared: Like button ──────────────────────────────────────────────────────
const LikeButton = ({ liked, likeCount, onLike, className, label, style }) => (
  <button onClick={onLike} className={className} style={style}>
    <svg className="w-4 h-4" fill={liked ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
    {likeCount} {label || (likeCount === 1 ? "Like" : "Likes")}
  </button>
);

// ─── Shared: Comments block (theme-styled) ────────────────────────────────────
const COMMENT_STYLES = {
  classic: {
    wrap: "mt-12", accent: "bg-blue-500", heading: "text-zinc-400", card: "bg-[#111116] border border-zinc-800",
    name: "text-white", date: "text-zinc-600", body: "text-zinc-300", empty: "text-zinc-600",
    input: "bg-zinc-900 border-zinc-700 text-white placeholder-zinc-600 focus:border-blue-500",
    btn: "bg-blue-600 hover:bg-blue-500 text-white", ok: "bg-blue-600/10 border-blue-600/30 text-blue-400",
  },
  modern: {
    wrap: "mt-12", accent: "bg-purple-500", heading: "text-zinc-400", card: "bg-white/3 border border-white/8",
    name: "text-white", date: "text-zinc-500", body: "text-zinc-300", empty: "text-zinc-500",
    input: "bg-white/5 border-white/10 text-white placeholder-zinc-600 focus:border-purple-500",
    btn: "bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white", ok: "bg-purple-600/10 border-purple-600/30 text-purple-300",
  },
  terminal: {
    wrap: "mt-8", accent: "bg-green-500", heading: "text-green-600", card: "bg-[#0a0e0a] border border-green-500/30",
    name: "text-green-400", date: "text-green-700", body: "text-green-300", empty: "text-green-700",
    input: "bg-black border-green-700 text-green-300 placeholder-green-800 focus:border-green-400 font-mono",
    btn: "bg-green-500/10 border border-green-500/50 hover:bg-green-500/20 text-green-400", ok: "bg-green-500/10 border-green-500/30 text-green-400",
  },
  fabrica: {
    wrap: "mt-12", accent: "bg-[#E85D04]", heading: "text-stone-400", card: "bg-stone-50 border border-stone-200",
    name: "text-stone-900", date: "text-stone-400", body: "text-stone-600", empty: "text-stone-400",
    input: "bg-white border-stone-300 text-stone-900 placeholder-stone-400 focus:border-[#E85D04]",
    btn: "bg-[#E85D04] hover:opacity-90 text-white", ok: "bg-orange-50 border-orange-200 text-orange-700",
  },
  aurora: {
    wrap: "mt-12", accent: "bg-[#22C55E]", heading: "text-slate-400", card: "bg-[rgba(15,23,42,0.7)] border border-[rgba(148,163,184,0.12)]",
    name: "text-white", date: "text-slate-500", body: "text-slate-300", empty: "text-slate-500",
    input: "bg-[rgba(15,23,42,0.8)] border-[rgba(148,163,184,0.2)] text-white placeholder-slate-600 focus:border-[#22C55E]",
    btn: "bg-[rgba(34,197,94,0.15)] border border-[rgba(34,197,94,0.3)] hover:bg-[rgba(34,197,94,0.25)] text-[#22C55E]", ok: "bg-[rgba(34,197,94,0.1)] border-[rgba(34,197,94,0.2)] text-[#22C55E]",
  },
  vanta: {
    wrap: "mt-12", accent: "bg-[#C9A84C]", heading: "text-[#8B9BB4]", card: "border border-[rgba(201,168,76,0.15)]",
    name: "text-[#F5ECD7]", date: "text-[#4A5568]", body: "text-[#8B9BB4]", empty: "text-[#4A5568]",
    input: "bg-transparent border-[rgba(201,168,76,0.2)] text-[#F5ECD7] placeholder-[#4A5568] focus:border-[#C9A84C]",
    btn: "bg-[rgba(201,168,76,0.12)] border border-[rgba(201,168,76,0.3)] hover:bg-[rgba(201,168,76,0.2)] text-[#C9A84C]", ok: "bg-[rgba(201,168,76,0.1)] border-[rgba(201,168,76,0.2)] text-[#C9A84C]",
  },
};

const CommentsBlock = ({ comments, commentForm, setCommentForm, onSubmit, submitting, submitted, theme }) => {
  const s = COMMENT_STYLES[theme] || COMMENT_STYLES.classic;
  const approved = comments; // backend already filters is_approved = 1
  const term = theme === "terminal";

  return (
    <div className={s.wrap}>
      <div className="flex items-center gap-3 mb-6">
        <span className={`w-1 h-5 rounded-full ${s.accent}`} />
        <h2 className={`text-sm font-black uppercase tracking-widest ${s.heading} ${term ? "font-mono normal-case tracking-normal" : ""}`}>
          {term ? `# comments (${approved.length})` : `Comments (${approved.length})`}
        </h2>
      </div>
      <div className="space-y-4 mb-8">
        {approved.map((c) => (
          <div key={c.id} className={`${s.card} rounded-xl p-4 ${term ? "font-mono" : ""}`}>
            <div className="flex items-center justify-between mb-2">
              <span className={`text-sm font-bold ${s.name}`}>{term ? `> ${c.name}` : c.name}</span>
              <span className={`text-[10px] font-mono ${s.date}`}>{fmtDate(c.created_at)}</span>
            </div>
            <p className={`text-sm leading-relaxed ${s.body}`}>{c.content}</p>
          </div>
        ))}
        {approved.length === 0 && <p className={`text-sm ${s.empty} ${term ? "font-mono" : ""}`}>{term ? "// no comments yet. be the first!" : "No comments yet. Be the first!"}</p>}
      </div>
      {submitted ? (
        <div className={`border rounded-xl p-4 text-sm font-semibold ${s.ok} ${term ? "font-mono" : ""}`}>
          {term ? "[ok] comment queued — pending moderation." : "Comment submitted! It'll appear after moderation."}
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-3">
          <input type="text" placeholder={term ? "$ name" : "Your name"} value={commentForm.name}
            onChange={(e) => setCommentForm((f) => ({ ...f, name: e.target.value }))}
            className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none transition-colors ${s.input}`} />
          <textarea placeholder={term ? "$ write a comment..." : "Write a comment..."} value={commentForm.content}
            onChange={(e) => setCommentForm((f) => ({ ...f, content: e.target.value }))} rows={4}
            className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none transition-colors resize-none ${s.input}`} />
          <button type="submit" disabled={submitting}
            className={`px-6 py-2.5 disabled:opacity-50 font-bold text-sm rounded-xl transition-all ${s.btn} ${term ? "font-mono" : ""}`}>
            {submitting ? (term ? "sending..." : "Submitting...") : term ? "./submit.sh" : "Submit Comment"}
          </button>
        </form>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// AURORA — dark cinematic glassmorphism
// ════════════════════════════════════════════════════════════════════════════
const AuroraLayout = (p) => {
  const { post, cover, liked, likeCount, onLike, comments, allPosts, profile, ...c } = p;
  const BG = "#020617"; const ACCENT = "#22C55E"; const MUTED = "#94A3B8";
  return (
    <div style={{ minHeight:"100vh", background:BG, color:"#F8FAFC", fontFamily:"'DM Sans', sans-serif" }}>
      {/* Ambient blobs */}
      <div aria-hidden="true" style={{ position:"fixed", inset:0, pointerEvents:"none", overflow:"hidden", zIndex:0 }}>
        <div style={{ position:"absolute", top:"-10%", right:"-10%", width:500, height:500, borderRadius:"50%",
          background:"radial-gradient(circle, rgba(34,197,94,0.06) 0%, transparent 70%)", filter:"blur(40px)" }} />
        <div style={{ position:"absolute", bottom:"10%", left:"-5%", width:400, height:400, borderRadius:"50%",
          background:"radial-gradient(circle, rgba(59,130,246,0.06) 0%, transparent 70%)", filter:"blur(40px)" }} />
        <div style={{ position:"absolute", inset:0,
          backgroundImage:"linear-gradient(rgba(148,163,184,0.02) 1px, transparent 1px),linear-gradient(90deg, rgba(148,163,184,0.02) 1px, transparent 1px)",
          backgroundSize:"64px 64px" }} />
      </div>
      {cover && (
        <div style={{ position:"relative", height:320, overflow:"hidden" }}>
          <img src={cover} alt={post.title} style={{ width:"100%", height:"100%", objectFit:"cover" }} />
          <div style={{ position:"absolute", inset:0, background:`linear-gradient(to bottom, transparent 40%, ${BG})` }} />
        </div>
      )}
      <div style={{ maxWidth:1200, margin:"0 auto", padding:"40px 24px", position:"relative", zIndex:1 }}>
        <Link to="/blog" style={{ display:"inline-flex", alignItems:"center", gap:8, color:MUTED, textDecoration:"none",
          fontSize:13, fontWeight:600, marginBottom:40 }}>
          <svg width={14} height={14} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to blog
        </Link>
        <div style={{ display:"flex", gap:40, alignItems:"flex-start" }} className="aurora-post-grid">
          {/* Main */}
          <div style={{ flex:1, minWidth:0 }}>
            {!cover && <h1 style={{ fontFamily:"'Space Grotesk', sans-serif", fontSize:"clamp(24px,5vw,40px)",
              fontWeight:700, lineHeight:1.2, marginBottom:16 }}>{post.title}</h1>}
            {cover && <h1 style={{ fontFamily:"'Space Grotesk', sans-serif", fontSize:"clamp(24px,5vw,40px)",
              fontWeight:700, lineHeight:1.2, marginBottom:16 }}>{post.title}</h1>}
            <div style={{ display:"flex", gap:16, fontSize:12, color:MUTED, marginBottom:40 }}>
              <span>{fmtDate(post.published_at, true)}</span>
              {post.view_count != null && <><span>·</span><span>{post.view_count} views</span></>}
            </div>
            <article style={{
              lineHeight:1.85, fontSize:15, color:"#CBD5E1",
              "--tw-prose-body":"#CBD5E1", "--tw-prose-headings":"#F8FAFC",
            }} className="prose prose-invert prose-sm max-w-none prose-headings:font-bold prose-a:text-[#22C55E] prose-code:bg-[rgba(34,197,94,0.1)] prose-code:text-[#22C55E] prose-code:px-1 prose-code:rounded prose-pre:bg-[rgba(15,23,42,0.9)] prose-pre:border prose-pre:border-[rgba(148,163,184,0.12)]"
              dangerouslySetInnerHTML={{ __html: renderContent(post.content) }} />
            <div style={{ marginTop:40 }}>
              <LikeButton liked={liked} likeCount={likeCount} onLike={onLike}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all border ${liked
                  ? "bg-[rgba(34,197,94,0.2)] border-[rgba(34,197,94,0.4)] text-[#22C55E]"
                  : "bg-[rgba(15,23,42,0.7)] border-[rgba(148,163,184,0.15)] text-slate-400 hover:border-[rgba(34,197,94,0.3)] hover:text-[#22C55E]"}`} />
            </div>
            <RelatedPosts currentSlug={post.slug} allPosts={allPosts} theme="aurora" />
            <CommentsBlock {...c} comments={comments} theme="aurora" />
          </div>
          {/* Sidebar */}
          <aside style={{ width:280, flexShrink:0, position:"sticky", top:80 }} className="aurora-post-aside">
            <RecentPostsSidebar currentSlug={post.slug} allPosts={allPosts} theme="aurora" />
            <div style={{ marginTop:16 }}>
              <LinkedInSidebar theme="aurora" profile={profile} />
            </div>
          </aside>
        </div>
      </div>
      <style>{`
        @media (max-width: 1024px) { .aurora-post-aside { display: none !important; } }
        @media (max-width: 768px)  { .aurora-post-grid  { flex-direction: column !important; } }
      `}</style>
    </div>
  );
};


// ─── Root ─────────────────────────────────────────────────────────────────────
const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [theme, setTheme] = useState("classic");
  const [comments, setComments] = useState([]);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [allPosts, setAllPosts] = useState([]);
  const [profile, setProfile] = useState(null);
  const [commentForm, setCommentForm] = useState({ name: "", content: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    trackVisit(`/blog/${slug}`);
    Promise.all([
      getPostBySlug(slug),
      getPublicComments(slug),
      getSettings().then((s) => s.active_theme ?? "classic").catch(() => "classic"),
      getPublishedPosts().catch(() => []),
      getProfile().catch(() => null),
    ])
      .then(([p, c, t, posts, prof]) => {
        setPost(p);
        setLikeCount(p.likes ?? p.like_count ?? 0);
        setLiked(!!p.liked);
        setComments(c);
        setTheme(t);
        setAllPosts(posts);
        setProfile(prof ?? null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  const handleLike = async () => {
    try {
      const res = await toggleLike(slug);
      setLiked(res.liked);
      setLikeCount(res.like_count ?? likeCount + (res.liked ? 1 : -1));
    } catch {}
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!commentForm.name.trim() || !commentForm.content.trim()) return;
    setSubmitting(true);
    try {
      await addComment(slug, commentForm);
      setSubmitted(true);
      setCommentForm({ name: "", content: "" });
    } catch {
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d0d10] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#0d0d10] text-zinc-600">
        <p className="text-sm font-semibold">Post not found</p>
        <Link to="/blog" className="mt-4 text-blue-400 text-sm hover:underline">← Back to blog</Link>
      </div>
    );
  }

  const layoutProps = {
    post,
    cover: resolveImg(post.cover_img),
    liked, likeCount, onLike: handleLike,
    comments, commentForm, setCommentForm, onSubmit: handleComment, submitting, submitted,
    allPosts,
    profile,
  };

  if (theme === "modern") return <ModernLayout {...layoutProps} />;
  if (theme === "terminal") return <TerminalLayout {...layoutProps} />;
  if (theme === "fabrica") return <FabricaLayout {...layoutProps} />;
  if (theme === "aurora") return <AuroraLayout {...layoutProps} />;
  return <ClassicLayout {...layoutProps} />;
};

export default BlogPost;
